/* Progressive enhancements. All cart mutations are handled by Shopify. */
(() => {
  const config = document.querySelector('[data-shopping-config]');
  if (!config || typeof fetch !== 'function' || typeof FormData !== 'function') return;
  const labels = config.dataset;
  const drawer = document.getElementById('NookCartDrawer');
  let cartBusy = false;
  let returnFocus;
  let messageTimer;

  function announce(message, error = false) {
    const status = drawer?.open
      ? drawer.querySelector('[data-cart-message]')
      : document.querySelector('[data-shop-status]');
    if (!status) return;
    clearTimeout(messageTimer);
    status.textContent = message;
    status.dataset.error = String(error);
    if (!error) messageTimer = setTimeout(() => { status.textContent = ''; }, 8000);
  }

  function showCart(trigger) {
    if (!drawer || typeof drawer.showModal !== 'function') return false;
    if (!drawer.open) {
      returnFocus = trigger || document.activeElement;
      drawer.showModal();
    }
    document.querySelectorAll('[data-cart-open]').forEach(link => link.setAttribute('aria-expanded', 'true'));
    return true;
  }

  function remapIds(root, prefix) {
    const ids = new Map();
    root.querySelectorAll('[id]').forEach(node => {
      ids.set(node.id, prefix + node.id);
      node.id = prefix + node.id;
    });
    root.querySelectorAll('[for], [aria-labelledby], [aria-describedby], [form]').forEach(node => {
      ['for', 'aria-labelledby', 'aria-describedby', 'form'].forEach(attr => {
        if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(/\s+/).map(id => ids.get(id) || id).join(' '));
      });
    });
  }

  async function refreshCart() {
    const response = await fetch(labels.cartUrl, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(labels.error);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = html.querySelector('[data-cart-context="page"]');
    if (!source) throw new Error(labels.error);
    document.querySelectorAll('[data-cart-content]').forEach(old => {
      const fresh = source.cloneNode(true);
      fresh.dataset.cartContext = old.dataset.cartContext;
      if (old.dataset.cartContext === 'drawer') remapIds(fresh, 'Drawer-');
      old.replaceWith(fresh);
    });
    document.querySelectorAll('[data-header-cart-count]').forEach(count => { count.textContent = source.dataset.cartCount; });
  }

  function setBusy(busy) {
    cartBusy = busy;
    document.querySelectorAll('[data-cart-content], [data-product-form]').forEach(region => {
      region.setAttribute('aria-busy', String(busy));
    });
    document.querySelectorAll('[data-cart-change] input, [data-cart-change] button, [data-cart-content] button[name="checkout"], [data-product-form] button[name="add"], [data-sticky-add] button').forEach(control => {
      if (busy) {
        control.dataset.wasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.dataset.wasDisabled !== undefined) {
        control.disabled = control.dataset.wasDisabled === 'true';
        delete control.dataset.wasDisabled;
      }
    });
  }

  async function mutateCart(endpoint, body, trigger, success, openDrawer = false) {
    if (cartBusy) return;
    setBusy(true);
    let serverError;
    const focusId = document.activeElement?.id;
    try {
      // Mutations are never retried automatically: a network error can follow a committed add.
      const response = await fetch(endpoint, { method: 'POST', credentials: 'same-origin', body });
      const result = await response.json();
      if (!response.ok) serverError = typeof result.description === 'string' ? result.description : labels.error;
      // Shopify can adjust quantities even when returning an inventory error.
      await refreshCart();
      if (openDrawer && !serverError) showCart(trigger);
      if (!openDrawer && focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      if (!openDrawer && !document.activeElement?.closest('[data-cart-content]') && drawer?.open) drawer.querySelector('[data-cart-close]').focus({ preventScroll: true });
      if (!openDrawer && document.activeElement === document.body && !drawer?.open) {
        const heading = document.querySelector('.nook-cart-page h1');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      }
      announce(serverError || success, Boolean(serverError));
    } catch {
      // A failed response cannot establish whether Shopify committed the mutation.
      announce(serverError || labels.refreshError, true);
    } finally {
      setBusy(false);
    }
  }

  if (drawer && typeof drawer.showModal === 'function') {
    document.querySelectorAll('[data-cart-open]').forEach(link => {
      link.setAttribute('aria-controls', drawer.id);
      link.setAttribute('aria-expanded', 'false');
      link.setAttribute('aria-haspopup', 'dialog');
    });
    drawer.querySelector('[data-cart-close]').addEventListener('click', () => drawer.close());
    drawer.addEventListener('close', () => {
      document.querySelectorAll('[data-cart-open]').forEach(link => link.setAttribute('aria-expanded', 'false'));
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    });
    drawer.addEventListener('click', event => {
      const rect = drawer.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) drawer.close();
    });
  }

  document.addEventListener('click', async event => {
    const open = event.target.closest('[data-cart-open]');
    if (open && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0 && drawer) {
      event.preventDefault();
      if (cartBusy) return;
      if (!showCart(open)) { window.location.assign(open.href); return; }
      setBusy(true);
      drawer.setAttribute('aria-busy', 'true');
      try { await refreshCart(); } catch { announce(labels.error, true); }
      finally { drawer.removeAttribute('aria-busy'); setBusy(false); }
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      if (cartBusy) return;
      const data = new FormData();
      data.set('id', remove.dataset.lineKey);
      data.set('quantity', '0');
      await mutateCart(labels.cartChangeUrl, data, remove, labels.updated);
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.submitter?.name === 'checkout' && cartBusy) { event.preventDefault(); return; }
    if (form.matches('[data-cart-change]')) {
      event.preventDefault();
      if (!form.reportValidity() || cartBusy) return;
      const data = new FormData(form);
      mutateCart(labels.cartChangeUrl, data, event.submitter, labels.updated);
    } else if (form.matches('[data-product-form]') && event.submitter?.name === 'add' && drawer && typeof drawer.showModal === 'function') {
      event.preventDefault();
      if (!form.reportValidity() || cartBusy) return;
      const data = new FormData(form);
      mutateCart(labels.cartAddUrl, data, event.submitter, labels.added, true);
    }
  });

  function safeLink(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && /^https?:$/.test(url.protocol) ? url.href : null;
    } catch { return null; }
  }

  function suggestionURL(endpoint, query, type = 'product,collection') {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', type);
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    return url;
  }

  async function suggestions(endpoint, query, signal, type) {
    const response = await fetch(suggestionURL(endpoint, query, type), { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error(labels.error);
    return (await response.json()).resources?.results || {};
  }

  function initSearch(scope = document) {
    if (labels.searchEnabled !== 'true') return;
    scope.querySelectorAll('[data-predictive-form]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const input = form.querySelector('[data-predictive-input]');
      const list = form.querySelector('[data-predictive-list]');
      const status = form.querySelector('[data-predictive-status]');
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', list.id);
      input.setAttribute('aria-expanded', 'false');
      let timer, controller, selected = -1, version = 0;
      function close() {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        selected = -1;
      }
      function cancel() {
        clearTimeout(timer);
        controller?.abort();
        version++;
        close();
        list.removeAttribute('aria-busy');
      }
      input.addEventListener('input', () => {
        cancel();
        const query = input.value.trim();
        if (!query) { status.textContent = ''; return; }
        const requestVersion = version;
        timer = setTimeout(async () => {
          controller = new AbortController();
          list.setAttribute('aria-busy', 'true');
          try {
            const results = await suggestions(form.dataset.predictiveUrl, query, controller.signal);
            if (requestVersion !== version || document.activeElement !== input) return;
            list.replaceChildren();
            const resources = [...(results.products || []).map(item => ({ ...item, label: labels.productLabel })), ...(results.collections || []).map(item => ({ ...item, label: labels.collectionLabel }))];
            resources.forEach((resource, index) => {
              const href = safeLink(resource.url);
              if (!href) return;
              const option = document.createElement('li');
              option.id = `${list.id}-${index}`;
              option.setAttribute('role', 'option');
              option.setAttribute('aria-selected', 'false');
              const link = document.createElement('a');
              link.href = href;
              link.tabIndex = -1;
              const title = document.createElement('span');
              title.textContent = resource.title;
              const kind = document.createElement('small');
              kind.textContent = resource.label;
              link.append(title, kind);
              option.append(link);
              list.append(option);
            });
            list.hidden = !list.children.length;
            input.setAttribute('aria-expanded', String(!list.hidden));
            status.textContent = list.hidden ? labels.noSuggestions : labels.suggestionsReady;
          } catch (error) {
            if (error.name !== 'AbortError' && requestVersion === version) { close(); status.textContent = labels.noSuggestions; }
          } finally { if (requestVersion === version) list.removeAttribute('aria-busy'); }
        }, 180);
      });
      input.addEventListener('keydown', event => {
        if (event.key === 'Escape') { cancel(); return; }
        if (list.hidden) return;
        const options = [...list.children];
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          selected = selected < 0
            ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
            : (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options.forEach((option, index) => option.setAttribute('aria-selected', String(index === selected)));
          input.setAttribute('aria-activedescendant', options[selected].id);
          options[selected].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && selected >= 0) {
          event.preventDefault();
          options[selected].querySelector('a').click();
        } else if (event.key === 'Tab') cancel();
      });
      form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) cancel(); });
      form.addEventListener('submit', cancel);
    });
  }

  function initMediaAndSticky() {
    document.querySelectorAll('.nook-external-video').forEach(details => {
      if (details.dataset.initialized) return;
      details.dataset.initialized = 'true';
      details.addEventListener('toggle', () => {
        const template = details.querySelector('template');
        if (details.open && template) { template.replaceWith(template.content.cloneNode(true)); }
      });
    });
    const sticky = document.querySelector('[data-sticky-add]');
    const mainAdd = document.querySelector('[data-main-add]');
    if (!sticky || !mainAdd || sticky.dataset.initialized || !('IntersectionObserver' in window)) return;
    sticky.dataset.initialized = 'true';
    new IntersectionObserver(entries => {
      sticky.hidden = entries[0].isIntersecting || entries[0].boundingClientRect.top >= 0;
      document.body.classList.toggle('nook-has-sticky', !sticky.hidden);
    }).observe(mainAdd);
  }

  initSearch();
  initMediaAndSticky();
  document.addEventListener('shopify:section:load', () => { initSearch(); initMediaAndSticky(); });
  document.addEventListener('shopify:block:select', () => { initSearch(); initMediaAndSticky(); });
  document.querySelectorAll('[data-collection-search]').forEach(async region => {
    if (labels.searchEnabled !== 'true') return;
    try {
      const result = await suggestions(region.dataset.predictiveUrl, region.dataset.query, undefined, 'collection');
      const target = region.querySelector('[data-collection-links]');
      (result.collections || []).forEach(collection => {
        const href = safeLink(collection.url);
        if (!href) return;
        const link = document.createElement('a');
        link.href = href;
        link.textContent = collection.title;
        target.append(link);
      });
      region.hidden = !target.children.length;
    } catch { /* Native product/page/article search remains available. */ }
  });
})();
